import React, { useState, useRef } from 'react';
import axios from 'axios';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';

const PropertyForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    locationDetail: '',
    category: '',
    glink: '',
    contact: '',
    roomsAvailability: 0,
    rate: 0,
    roomType: '',
    mealPlan: '',
    pool: false,
    restaurant: false,
    images: [],
    rating: 0,
    totalRatings: 0,
    ratingText: '',
    tags: [],
    amenities: [],
    originalPrice: 0,
    currentPrice: 0,
    taxes: 0,
    offer: '',
    description: '',
    isActive: true,
    featured: false
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('error');
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Validate required fields
    if (!formData.name.trim()) {
      setMessage('Hotel name is required');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (!formData.location.trim()) {
      setMessage('Location is required');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (!formData.category) {
      setMessage('Category is required');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (!formData.contact.trim()) {
      setMessage('Contact number is required');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (formData.rating < 1 || formData.rating > 5) {
      setMessage('Rating must be between 1 and 5');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (formData.currentPrice <= 0) {
      setMessage('Current price must be greater than 0');
      setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      // Convert string values to appropriate types
      const submitData = {
        ...formData,
        category: (formData.category || '').toLowerCase(),
        roomsAvailability: parseInt(formData.roomsAvailability) || 0,
        rate: parseFloat(formData.rate) || 0,
        rating: parseFloat(formData.rating) || 0,
        totalRatings: parseInt(formData.totalRatings) || 0,
        originalPrice: parseInt(formData.originalPrice) || 0,
        currentPrice: parseInt(formData.currentPrice) || 0,
        taxes: parseInt(formData.taxes) || 0
      };

      // Use direct API call instead of hotelsAPI
      const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://api.goadeals.in/api').replace(/^http:\/\/api\.goadeals\.in/i, 'https://api.goadeals.in');
      const response = await fetch(`${apiBaseUrl}/hotels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const result = await response.json();
        setMessage('Hotel added successfully! Your property is now live on our platform.');
        setMessageType('success');
        if (onSuccess) onSuccess();
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add hotel. Please try again.');
      }
    } catch (error) {
      console.error('Error adding hotel:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setMessage('Unable to connect to server. Please check your internet connection and try again.');
      } else {
        setMessage(error.message || 'An unexpected error occurred. Please try again.');
      }
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setLoading(true);
    try {
      const uploadedUrls = [];
      
      const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://api.goadeals.in/api').replace(/^http:\/\/api\.goadeals\.in/i, 'https://api.goadeals.in');
      const apiUrl = apiBaseUrl.replace('/api', ''); // Remove /api for base URL since we add it in the path
      
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', file);
        
        const response = await axios.post(`${apiBaseUrl}/upload/image`, formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Get the full URL (backend URL + uploaded path)
        const imageUrl = `${apiUrl}${response.data.url}`;
        uploadedUrls.push(imageUrl);
      }
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage('Error uploading image. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
    
    // Reset file input
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    const tag = prompt('Enter tag:');
    if (tag) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const removeTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const addAmenity = () => {
    const amenity = prompt('Enter amenity:');
    if (amenity) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, amenity]
      }));
    }
  };

  const removeAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="property-form-overlay">
      <div className="property-form-container">
        <div className="property-form-header">
          <h2>Add Your Property</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="property-form">
          <ErrorMessage 
            message={message} 
            type={messageType} 
            onClose={() => setMessage('')}
            show={!!message}
          />

          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Hotel Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter hotel name"
                />
              </div>
              <div className="form-group">
                <label>Location *</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder="City, State"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Location Detail</label>
              <input
                type="text"
                name="locationDetail"
                value={formData.locationDetail}
                onChange={handleChange}
                placeholder="Specific location details"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category *</label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Category</option>
                          <option value="hotel">Hotel</option>
                          <option value="villa">Villa</option>
                          <option value="homestay">Homestay</option>
                        </select>
              </div>
              <div className="form-group">
                <label>Contact Number *</label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  required
                  placeholder="945 945 7733"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Property Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Rooms Available *</label>
                <input
                  type="number"
                  name="roomsAvailability"
                  value={formData.roomsAvailability}
                  onChange={handleChange}
                  required
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Room Type</label>
                <input
                  type="text"
                  name="roomType"
                  value={formData.roomType}
                  onChange={handleChange}
                  placeholder="Deluxe, Suite, etc."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Meal Plan</label>
                <select
                  name="mealPlan"
                  value={formData.mealPlan}
                  onChange={handleChange}
                >
                  <option value="">Select Meal Plan</option>
                  <option value="Breakfast Included">Breakfast Included</option>
                  <option value="All Meals Included">All Meals Included</option>
                  <option value="Breakfast & Dinner">Breakfast & Dinner</option>
                  <option value="No Meals">No Meals</option>
                </select>
              </div>
              <div className="form-group">
                <label>Google Maps Link</label>
                <input
                  type="url"
                  name="glink"
                  value={formData.glink}
                  onChange={handleChange}
                  placeholder="https://maps.google.com/place/..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="pool"
                    checked={formData.pool}
                    onChange={handleChange}
                  />
                  Swimming Pool Available
                </label>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="restaurant"
                    checked={formData.restaurant}
                    onChange={handleChange}
                  />
                  Restaurant Available
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Pricing & Ratings</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Original Price (₹) *</label>
                <input
                  type="number"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  required
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Current Price (₹) *</label>
                <input
                  type="number"
                  name="currentPrice"
                  value={formData.currentPrice}
                  onChange={handleChange}
                  required
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Taxes (₹)</label>
                <input
                  type="number"
                  name="taxes"
                  value={formData.taxes}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Special Offer</label>
                <input
                  type="text"
                  name="offer"
                  value={formData.offer}
                  onChange={handleChange}
                  placeholder="Early Bird, Limited Time, etc."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Rating (1-5) *</label>
                <input
                  type="number"
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                  required
                  min="1"
                  max="5"
                  step="0.1"
                />
              </div>
              <div className="form-group">
                <label>Total Ratings</label>
                <input
                  type="number"
                  name="totalRatings"
                  value={formData.totalRatings}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Rating Text</label>
              <input
                type="text"
                name="ratingText"
                value={formData.ratingText}
                onChange={handleChange}
                placeholder="Excellent, Very Good, etc."
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Images</h3>
            <div className="form-group">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                id="property-image-upload"
              />
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                style={{ marginBottom: '10px' }}
              >
                Choose Images
              </button>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '10px' }}>
                Select one or more images to upload (JPEG, PNG, GIF, WebP - Max 5MB each)
              </p>
              <div className="image-list">
                {formData.images.map((image, index) => (
                  <div key={index} className="image-item">
                    <img src={image} alt={`Property ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => removeImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Tags & Amenities</h3>
            <div className="form-group">
              <label>Tags</label>
              <button type="button" className="btn btn-secondary" onClick={addTag}>
                Add Tag
              </button>
              <div className="tags-list">
                {formData.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(index)}
                      className="remove-tag"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Amenities</label>
              <button type="button" className="btn btn-secondary" onClick={addAmenity}>
                Add Amenity
              </button>
              <div className="amenities-list">
                {formData.amenities.map((amenity, index) => (
                  <div key={index} className="amenity-item">
                    {amenity}
                    <button
                      type="button"
                      onClick={() => removeAmenity(index)}
                      className="remove-amenity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Description</h3>
            <div className="form-group">
              <label>Hotel Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                placeholder="Describe your property..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <LoadingSpinner size="small" text="Adding Property..." showText={false} />
              ) : (
                'Add Property'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyForm;
